<template>
  <b-modal v-model="show" size="lg" :title="$t('pushWeSign.title')"
           no-close-on-backdrop no-close-on-esc hide-footer>
    <div v-if="loading" class="text-center py-4">
      <font-awesome-icon icon="spinner" spin/> Đang tải...
    </div>
    <div v-else-if="kyc">
      <div class="alert alert-info small">
        <font-awesome-icon icon="info-circle"/>&nbsp;
        Hệ thống sẽ render PDF từ template + upload lên MISA WeSign + tạo signflow.
        Khách nhận link ký qua email theo flow MISA. KYC sẽ <strong>không tự động duyệt</strong>
        khi khách ký xong — admin xem PDF đã ký rồi tự click "Duyệt".
      </div>

      <!-- Section 1: Template + Contract Number -->
      <div class="form-group">
        <label>{{ $t('pushWeSign.selectTemplate') }} <span class="text-danger">*</span></label>
        <b-form-select v-model="templateId" :options="templateOptions"></b-form-select>
      </div>

      <div class="form-group">
        <label>{{ $t('pushWeSign.contractNumber') }} <span class="text-danger">*</span></label>
        <b-form-input v-model="contractNumber" placeholder="VD: HD-202605-001"></b-form-input>
        <small class="text-muted">Nếu khác số HĐ đang lưu sẽ tự cập nhật vào KYC.</small>
      </div>

      <!-- Section 2: Participants -->
      <div class="form-group">
        <label class="d-flex align-items-center">
          {{ $t('pushWeSign.participants') }} <span class="text-danger">&nbsp;*</span>
          <button class="btn btn-sm btn-outline-primary ml-auto" @click="addParticipant">
            <font-awesome-icon icon="plus"/>&nbsp;Thêm người ký
          </button>
        </label>
        <div class="table-responsive">
          <table class="table table-sm table-bordered">
            <thead class="thead-light">
              <tr>
                <th style="width: 32px">#</th>
                <th>Họ tên *</th>
                <th>Email</th>
                <th>SĐT</th>
                <th style="width: 90px">Thứ tự</th>
                <th style="width: 50px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, idx) in participants" :key="idx">
                <td class="align-middle text-center">{{ idx + 1 }}</td>
                <td><b-form-input size="sm" v-model="p.name" placeholder="Nguyễn Văn A"/></td>
                <td><b-form-input size="sm" v-model="p.email" type="email" placeholder="a@example.com"/></td>
                <td><b-form-input size="sm" v-model="p.mobile" placeholder="0901234567"/></td>
                <td><b-form-input size="sm" v-model.number="p.priority" type="number" min="1"/></td>
                <td class="align-middle text-center">
                  <button v-if="participants.length > 1" class="btn btn-sm btn-link text-danger p-0"
                          @click="removeParticipant(idx)">
                    <font-awesome-icon icon="times"/>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <small class="text-muted">Cần ít nhất 1 người ký. Mỗi người cần có email hoặc SĐT.</small>
      </div>

      <!-- Section 3: Order sign -->
      <div class="form-group">
        <b-form-checkbox v-model="isOrderSign" switch>
          {{ $t('pushWeSign.isOrderSign') }}
          <small class="text-muted ml-2">Khách ký theo thứ tự "Thứ tự" trong bảng trên</small>
        </b-form-checkbox>
      </div>

      <!-- Result: signLinks sau khi push thành công -->
      <div v-if="signLinks.length" class="mt-3 alert alert-success">
        <strong>
          <font-awesome-icon icon="check-circle"/>&nbsp;Đã đẩy lên MISA WeSign!
        </strong>
        <div class="mt-2">Copy link bên dưới để gửi cho khách qua email/Zalo:</div>
        <table class="table table-sm mt-2 mb-0 bg-white">
          <thead>
            <tr>
              <th>Người ký</th>
              <th>Link ký</th>
              <th style="width: 50px"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(l, idx) in signLinks" :key="idx">
              <td>
                <div>{{ l.name }}</div>
                <small class="text-muted">{{ l.email }}</small>
              </td>
              <td><code class="small">{{ l.link }}</code></td>
              <td>
                <button class="btn btn-sm btn-link p-0" @click="copyLink(l.link)">
                  <font-awesome-icon icon="copy"/>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="d-flex mt-3">
      <button class="btn btn-light" @click="close" :disabled="pushing">
        {{ signLinks.length ? 'Đóng' : 'Huỷ' }}
      </button>
      <button v-if="!signLinks.length" class="btn btn-primary ml-auto" @click="submit"
              :disabled="pushing || !canSubmit">
        <font-awesome-icon v-if="pushing" icon="spinner" spin/>
        <font-awesome-icon v-else icon="paper-plane"/>&nbsp;{{ $t('pushWeSign.push') }}
      </button>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'PushWeSignModal',
  data() {
    return {
      show: false,
      loading: false,
      pushing: false,
      kyc: null,
      templates: [],
      defaults: {},
      templateId: null,
      contractNumber: '',
      participants: [],
      isOrderSign: false,
      signLinks: [],
    };
  },
  i18n: {
    messages: {
      vi: {
        pushWeSign: {
          title: 'Đẩy hợp đồng KYC lên MISA WeSign',
          selectTemplate: 'Chọn mẫu hợp đồng',
          contractNumber: 'Số hợp đồng',
          participants: 'Người ký',
          isOrderSign: 'Ký theo thứ tự',
          push: 'Đẩy MISA WeSign',
        },
      },
      en: {
        pushWeSign: {
          title: 'Push KYC contract to MISA WeSign',
          selectTemplate: 'Select contract template',
          contractNumber: 'Contract number',
          participants: 'Signers',
          isOrderSign: 'Sign in order',
          push: 'Push to MISA WeSign',
        },
      },
    },
  },
  computed: {
    templateOptions() {
      return [
        {value: null, text: '— Chọn mẫu —'},
        ...this.templates.map(t => ({value: t.id, text: t.name})),
      ];
    },
    canSubmit() {
      if (!this.templateId) return false;
      if (!this.contractNumber || !this.contractNumber.trim()) return false;
      if (!this.participants.length) return false;
      for (const p of this.participants) {
        if (!p.name || !p.name.trim()) return false;
        if (!p.email && !p.mobile) return false;
      }
      return true;
    },
  },
  methods: {
    open(kycId) {
      this.show = true;
      this.loading = true;
      this.signLinks = [];
      this.kyc = null;
      this.participants = [{name: '', email: '', mobile: '', priority: 1}];
      this.templateId = null;
      this.contractNumber = '';
      this.isOrderSign = false;

      const loadKyc = this.$service.get(`/client-kyc/${kycId}?populateClient=true`);
      const loadDefaults = this.$service.get(`/client-kyc/${kycId}/contract-defaults`);
      Promise.all([loadKyc, loadDefaults]).then(([kycRes, defRes]) => {
        this.kyc = kycRes.data;
        this.defaults = defRes.data || {};
        this.contractNumber = (this.kyc && this.kyc.contractNumber) || '';
        // Load templates theo type
        this.$service.get(`/client-kyc/contract-templates?type=${this.kyc.type}`).then(tplRes => {
          this.templates = tplRes.data || [];
          if (this.templates.length === 1) {
            this.templateId = this.templates[0].id;
          }
        });
        // Prefill 1 participant từ KYC nếu có signerEmail/name
        if (this.kyc.signerEmail || this.kyc.name) {
          this.participants = [{
            name: this.kyc.name || '',
            email: this.kyc.signerEmail || (this.kyc.invoiceEmails || [])[0] || '',
            mobile: '',
            priority: 1,
          }];
        }
      }).catch(err => {
        const msg = err.response && err.response.data && err.response.data.message;
        this.$swal({text: msg || 'Không tải được KYC', icon: 'error'});
        this.show = false;
      }).finally(() => {
        this.loading = false;
      });
    },
    close() {
      this.show = false;
      this.$emit('done', this.signLinks.length > 0);
    },
    addParticipant() {
      this.participants.push({
        name: '', email: '', mobile: '',
        priority: this.participants.length + 1,
      });
    },
    removeParticipant(idx) {
      this.participants.splice(idx, 1);
    },
    async submit() {
      if (!this.canSubmit) return;
      this.pushing = true;
      try {
        const response = await this.$service.post(
          `/client-kyc/${this.kyc._id}/push-wesign`,
          {
            templateId: this.templateId,
            contractNumber: this.contractNumber.trim(),
            participants: this.participants.map(p => ({
              name: p.name.trim(),
              email: p.email ? p.email.trim() : undefined,
              mobile: p.mobile ? p.mobile.trim() : undefined,
              role: 0,
              priority: p.priority || 1,
            })),
            isOrderSign: this.isOrderSign,
          }
        );
        this.signLinks = (response.data && response.data.signLinks) || [];
        this.$swal({
          title: 'Thành công',
          text: 'Đã đẩy lên MISA WeSign. Copy link ký để gửi cho khách.',
          icon: 'success',
        });
      } catch (err) {
        const msg = err.response && err.response.data && err.response.data.message;
        this.$swal({text: msg || 'Đẩy MISA thất bại', icon: 'error'});
      } finally {
        this.pushing = false;
      }
    },
    copyLink(link) {
      const ta = document.createElement('textarea');
      ta.value = link;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      this.$swal({title: 'Đã copy', icon: 'success', timer: 1500, toast: true, position: 'top-end'});
    },
  },
};
</script>
